/**
 * §P.1 fase 2 · Las lesiones de un alumno.
 *
 * ## Por defecto solo las abiertas
 *
 * Es lo que hace útil esta sección: **quién sigue sin poder entrenar**, que es lo que
 * el maestro mira antes de montar la clase del martes. El histórico está a un clic,
 * pero de entrada no estorba.
 *
 * ## La fecha prevista no da el alta
 *
 * `return_expected_on` es una previsión y **no cierra nada**. El alta la da una
 * persona poniendo `returned_on`, y el contrato lo dice con estas palabras: «una
 * previsión no es un alta, y darla por buena devolvería a la actividad a alguien que
 * nadie ha revisado».
 *
 * Lo que sí hace la pantalla es avisar cuando la previsión ya pasó y sigue sin alta:
 * no significa que esté peor, significa que hay una llamada pendiente con la familia
 * y que nadie la va a hacer si la fecha prevista se sigue enseñando como si valiera.
 *
 * ## La registra quien la ve
 *
 * Y casi siempre es el entrenador, así que las tres rutas aceptan `coa`. Es la misma
 * asimetría que la ficha médica: quien está delante cuando pasa algo es quien lo
 * apunta.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError } from '@/shared/ui/Estados';
import { fecha, fechaParaInput } from '@/shared/ui/formato';
import {
  GRAVEDADES,
  pintaDeGravedad,
  vueltaVencida,
  type Gravedad,
  type Lesion,
} from '@/club/alumnos/salud/tipos';

export function Lesiones({
  club,
  alumnoId,
  puedeEditar,
}: {
  club: string;
  alumnoId: string;
  puedeEditar: boolean;
}) {
  const cache = useQueryClient();
  const [conHistorico, setConHistorico] = useState(false);
  const [registrando, setRegistrando] = useState(false);
  const [cuando, setCuando] = useState(() => fechaParaInput(new Date()));
  const [descripcion, setDescripcion] = useState('');
  const [gravedad, setGravedad] = useState<Gravedad | ''>('');
  const [prevista, setPrevista] = useState('');
  const [problema, setProblema] = useState<Problema | null>(null);

  const clave = ['lesiones', club, alumnoId, conHistorico] as const;

  const lesiones = useQuery({
    queryKey: clave,
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        .students({ studentId: alumnoId })
        .injuries.get({ query: conHistorico ? { healed: true } : {} });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Lesion[];
    },
  });

  const invalidar = () => {
    void cache.invalidateQueries({ queryKey: ['lesiones', club, alumnoId] });
  };

  const registrar = useMutation({
    mutationFn: async () => {
      const r = await api.v1
        .clubs({ club })
        .students({ studentId: alumnoId })
        .injuries.post({
          occurred_on: cuando,
          description: descripcion.trim(),
          severity: gravedad === '' ? null : gravedad,
          return_expected_on: prevista === '' ? null : prevista,
        });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setRegistrando(false);
      setDescripcion('');
      setGravedad('');
      setPrevista('');
      invalidar();
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  const alta = useMutation({
    mutationFn: async (v: { id: string; returned_on: string | null }) => {
      const r = await api.v1
        .clubs({ club })
        .students({ studentId: alumnoId })
        .injuries({ injuryId: v.id })
        .patch({ returned_on: v.returned_on });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: invalidar,
    onError: (e) => setProblema(aProblema(e)),
  });

  if (registrando) {
    return (
      <form
        className="formulario"
        onSubmit={(e) => {
          e.preventDefault();
          setProblema(null);
          registrar.mutate();
        }}
      >
        <div className="rejilla">
          <label>
            Cuándo pasó
            <input
              required
              type="date"
              value={cuando}
              onChange={(e) => setCuando(e.target.value)}
            />
          </label>

          <label>
            Gravedad
            <select
              value={gravedad}
              onChange={(e) => setGravedad(e.target.value as Gravedad | '')}
            >
              <option value="">Sin clasificar</option>
              {GRAVEDADES.map((g) => (
                <option key={g.valor} value={g.valor}>
                  {g.etiqueta}
                </option>
              ))}
            </select>
          </label>

          <label>
            {/* Previsión, no alta. El texto lo dice para que nadie lo confunda. */}
            Cuándo se prevé la vuelta
            <input
              type="date"
              value={prevista}
              onChange={(e) => setPrevista(e.target.value)}
            />
          </label>
        </div>

        <label>
          Qué ha pasado
          <textarea
            required
            rows={3}
            maxLength={1000}
            placeholder="Esguince de tobillo izquierdo haciendo un salto"
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
          />
        </label>

        <p className="tenue">
          La fecha prevista <strong>no da el alta</strong>: cuando vuelva, hay
          que decirlo aquí. Así la lista dice de verdad quién no puede entrenar.
        </p>

        {problema && <PanelError problema={problema} />}

        <div className="acciones">
          <button type="submit" disabled={registrar.isPending}>
            {registrar.isPending ? 'Guardando…' : 'Registrar la lesión'}
          </button>
          <button
            type="button"
            className="btn-plano"
            disabled={registrar.isPending}
            onClick={() => {
              setRegistrando(false);
              setProblema(null);
            }}
          >
            Cancelar
          </button>
        </div>
      </form>
    );
  }

  if (lesiones.isPending) {
    return <Cargando filas={2} etiqueta="Cargando las lesiones" />;
  }

  if (lesiones.isError || !lesiones.data) {
    return (
      <PanelError
        problema={aProblema(lesiones.error)}
        reintentar={() => void lesiones.refetch()}
      />
    );
  }

  const lista = lesiones.data;
  const abiertas = lista.filter((l) => l.active);

  return (
    <div>
      <div className="tutor-acciones" style={{ marginBlockStart: 0 }}>
        {puedeEditar && (
          <button type="button" onClick={() => setRegistrando(true)}>
            Registrar una lesión
          </button>
        )}
        <button
          type="button"
          className="btn-plano"
          onClick={() => setConHistorico((x) => !x)}
        >
          {conHistorico ? 'Solo las abiertas' : 'Ver también las cerradas'}
        </button>
      </div>

      {problema && <PanelError problema={problema} />}

      {lista.length === 0 ? (
        <p className="tenue">
          {conHistorico
            ? 'No hay ninguna lesión registrada.'
            : 'Ninguna lesión abierta. Puede entrenar con normalidad.'}
        </p>
      ) : (
        <ul className="lesiones">
          {lista.map((l) => {
            const pinta = pintaDeGravedad(l.severity);
            const vencida = vueltaVencida(l);

            return (
              <li key={l.id} className={l.active ? undefined : 'fila-fuera'}>
                <div className="aviso-cabeza">
                  {l.active ? (
                    <span className="pastilla pastilla-mal">Sin alta</span>
                  ) : (
                    <span className="pastilla pastilla-bien">De alta</span>
                  )}
                  {pinta && (
                    <span className={`pastilla ${pinta.clase}`}>
                      {pinta.etiqueta}
                    </span>
                  )}
                  <span className="tenue">{fecha(l.occurred_on)}</span>
                </div>

                <p>{l.description}</p>

                {/*
                  Lo que la pantalla añade al dato: la previsión pasó y sigue sin
                  alta. No es que esté peor, es que hay una llamada pendiente.
                */}
                {vencida && (
                  <p className="aviso" role="status">
                    <strong>
                      La vuelta se preveía para el {fecha(l.return_expected_on)}{' '}
                      y sigue sin alta.
                    </strong>{' '}
                    Conviene preguntar en casa antes de contar con ella para el
                    próximo examen o competición.
                  </p>
                )}

                <div className="aviso-pie">
                  <span className="tenue">
                    {l.returned_on
                      ? `Alta el ${fecha(l.returned_on)}`
                      : l.return_expected_on && !vencida
                        ? `Vuelta prevista el ${fecha(l.return_expected_on)}`
                        : l.return_expected_on
                          ? ''
                          : 'Sin fecha de vuelta prevista'}
                  </span>

                  {puedeEditar && (
                    <span className="tutor-acciones" style={{ margin: 0 }}>
                      {l.active ? (
                        <button
                          type="button"
                          className="btn-plano"
                          disabled={alta.isPending}
                          onClick={() =>
                            alta.mutate({
                              id: l.id,
                              returned_on: fechaParaInput(new Date()),
                            })
                          }
                        >
                          Darle el alta hoy
                        </button>
                      ) : (
                        // Reabrir: `returned_on` a `null`. Pasa cuando recae.
                        <button
                          type="button"
                          className="btn-plano"
                          disabled={alta.isPending}
                          onClick={() =>
                            alta.mutate({ id: l.id, returned_on: null })
                          }
                        >
                          Reabrirla
                        </button>
                      )}
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* El recuento, solo cuando dice algo. */}
      {abiertas.length > 0 && conHistorico && (
        <p className="tenue">
          {abiertas.length === 1
            ? 'Una lesión sigue abierta.'
            : `${abiertas.length} lesiones siguen abiertas.`}
        </p>
      )}
    </div>
  );
}
